import React from 'react'
import { styled } from '@mui/material/styles'
import TreeItem, { TreeItemProps } from '@mui/lab/TreeItem'
import Typography from '@mui/material/Typography'
import { SvgIconProps } from '@mui/material/SvgIcon'

const PREFIX = 'StyledTreeItem'

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  group: `${PREFIX}-group`,
  label: `${PREFIX}-label`,
}

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {
    color: theme.palette.text.secondary,

    '&:hover > $content': {
      backgroundColor: theme.palette.action.hover,
    },

    '&:focus > $content, &$selected > $content': {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.grey[400]})`,
      color: 'var(--tree-view-color)',
    },

    '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
      backgroundColor: 'transparent',
    },
  },

  [`& .${classes.content}`]: {
    borderBottomRightRadius: theme.spacing(2),
    borderTopRightRadius: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    paddingRight: theme.spacing(1),

    '$expanded > &': {
      fontWeight: theme.typography.fontWeightRegular,
    },
  },

  [`& .${classes.group}`]: {
    marginLeft: 7,
    paddingLeft: theme.spacing(2),
  },

  [`& .${classes.label}`]: {
    color: 'inherit',
    fontWeight: 'inherit',
  },
}))

declare module 'csstype' {
  interface Properties {
    '--tree-view-color'?: string
    '--tree-view-bg-color'?: string
  }
}

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string
  color?: string
  labelIcon?: React.ElementType<SvgIconProps>
  labelInfo?: string | null | undefined
  labelText: string
  onIconClick?(): void
  onLabelClick?(): void
}

export function StyledTreeItem(props: StyledTreeItemProps) {
  const { labelText, labelIcon: LabelIcon, labelInfo, color, bgColor, ...other } = props

  return (
    <TreeItem
      label={
        <Root
          sx={{
            alignItems: 'center',
            display: 'flex',
            padding: 0.5,
          }}
        >
          {LabelIcon && <LabelIcon color="inherit" sx={{ marginRight: 1 }} />}
          <Typography
            variant="body2"
            sx={{
              flexGrow: 1,
              fontWeight: 'inherit',
            }}
          >
            {labelText}
          </Typography>
          <Typography variant="caption" color="inherit">
            {labelInfo}
          </Typography>
        </Root>
      }
      style={{
        // @ts-ignore
        '--tree-view-color': color,
        '--tree-view-bg-color': bgColor,
      }}
      classes={{
        root: classes.root,
        content: classes.content,
        group: classes.group,
        label: classes.label,
      }}
      {...other}
    />
  )
}
